import React from 'react'

import clsx from 'clsx'
import { Calendar, CreditCard, Gift, Home, MapPin, Receipt, User } from 'lucide-react'

export type ProfileTabKey = 'home' | 'orders' | 'profile' | 'payment' | 'addresses' | 'reservations' | 'giftCards'

interface TabDefinition {
  key: ProfileTabKey
  label: string
}

const TABS: TabDefinition[] = [
  { key: 'home', label: 'Home' },
  { key: 'orders', label: 'Orders' },
  { key: 'profile', label: 'Profile' },
  { key: 'payment', label: 'Payment' },
  { key: 'addresses', label: 'Addresses' },
  { key: 'reservations', label: 'Reservations' },
  { key: 'giftCards', label: 'Gift Cards' },
]

const TAB_SLUGS: Record<ProfileTabKey, string> = {
  home: '',
  orders: 'orders',
  profile: 'edit',
  payment: 'payment',
  addresses: 'addresses',
  reservations: 'reservations',
  giftCards: 'gift-cards',
}

export const slugFromTabKey = (tab: ProfileTabKey): string => TAB_SLUGS[tab]

export const tabKeyFromSlug = (slug: string | undefined): ProfileTabKey => {
  const normalizedSlug = slug?.replace(/\/+$/, '') ?? ''
  const match = (Object.keys(TAB_SLUGS) as ProfileTabKey[]).find((key) => TAB_SLUGS[key] === normalizedSlug)

  return match ?? 'home'
}

const ICONS: Record<ProfileTabKey, React.ReactNode> = {
  home: <Home size={20} strokeWidth={1.75} />,
  orders: <Receipt size={20} strokeWidth={1.75} />,
  profile: <User size={20} strokeWidth={1.75} />,
  payment: <CreditCard size={20} strokeWidth={1.75} />,
  addresses: <MapPin size={20} strokeWidth={1.75} />,
  reservations: <Calendar size={20} strokeWidth={1.75} />,
  giftCards: <Gift size={20} strokeWidth={1.75} />,
}

interface TabNavProps {
  activeTab: ProfileTabKey
  onChange: (tab: ProfileTabKey) => void
  variant: 'chips' | 'rail'
}

export const TabNav: React.FC<TabNavProps> = ({ activeTab, onChange, variant }) => {
  return (
    <nav
      aria-label="Profile sections"
      className={clsx('user-profile-nav', variant === 'chips' ? 'user-profile-nav_chips' : 'user-profile-nav_rail')}
      role="tablist"
    >
      {TABS.map((tab) => (
        <button
          key={tab.key}
          aria-selected={activeTab === tab.key}
          className={clsx('user-profile-nav__item', activeTab === tab.key && 'user-profile-nav__item_current')}
          role="tab"
          type="button"
          onClick={() => onChange(tab.key)}
        >
          {ICONS[tab.key]}
          {tab.label}
        </button>
      ))}
    </nav>
  )
}
